import { RefObject, useEffect } from 'react';

/** One event-driven frame coordinates hero depth, chapter progress, and navigation. */
export function useScrollChoreography(rootRef: RefObject<HTMLDivElement>, disabled: boolean) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    let frame = 0;
    const hero = root.querySelector<HTMLElement>('.cx-hero-shell');
    const company = root.querySelector<HTMLElement>('#company');
    const chapters = Array.from(root.querySelectorAll<HTMLElement>('main section[id]'));
    const nav = Array.from(root.querySelectorAll<HTMLAnchorElement>('.cx-nav a'));
    const clamp = (n: number) => Math.max(0, Math.min(1, n));
    const paint = () => {
      frame = 0;
      const scroll = window.scrollY;
      const progress = clamp(scroll / Math.max(1, document.documentElement.scrollHeight - window.innerHeight));
      root.style.setProperty('--reading-progress', `${progress}`);
      const heroProgress = hero ? clamp(-hero.getBoundingClientRect().top / Math.max(1, hero.offsetHeight - window.innerHeight + window.innerHeight * .35)) : 0;
      root.style.setProperty('--hero-progress', disabled ? '0' : `${heroProgress}`);
      if (company) {
        const bounds = company.getBoundingClientRect();
        root.style.setProperty('--company-progress', disabled ? '1' : `${clamp((window.innerHeight - bounds.top) / (bounds.height + window.innerHeight * .3))}`);
      }
      let active = 'top';
      for (const chapter of chapters) if (chapter.getBoundingClientRect().top <= window.innerHeight * .4) active = chapter.id;
      for (const link of nav) {
        if (link.hash === `#${active}`) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      }
    };
    const schedule = () => { if (!frame && !document.hidden) frame = requestAnimationFrame(paint); };
    const resize = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    resize?.observe(root);
    paint();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('visibilitychange', schedule);
    return () => {
      cancelAnimationFrame(frame);
      resize?.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('visibilitychange', schedule);
    };
  }, [rootRef, disabled]);
}
